import { Info } from '@mui/icons-material';
import { Autocomplete, Box, Button, CircularProgress, Divider, Grid, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { USER_TYPES } from '../../config/dataService/constants';
import { MuiDisabledTextField, MuiSelectWithFocus, MuiTextFieldWithFocus } from '../../config/MaterialUIConstants';
import { checkFleetNumberExists } from '../../redux/Vehicles/action';
import {
    coreMasterKeys,
    useCreateVehicleMake,
    useCreateVehicleModel,
    useCreateVehicleTrim,
    useDecodeLicense,
    useDecodeVIN,
    useGetVehicleMakeList,
    useGetVehicleModelList,
    useGetVehicleTrimList,
    useGetVehicleYearMasterList,
} from '../../services/core/master/master.hooks';
import { CustomPaper } from '../../utility/SelectDropdown';
import useDebounce from '../../utility/useDebounce';
import { checkValidVIN } from '../../utility/utilityFunctions';
import LicensePlateInputV4 from '../LicensePlateInputV4';

// Style constants
const STYLES = {
    label: {
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        color: '#212121',
    },
    divider: {
        width: '100%',
        paddingLeft: '24px',
        marginTop: '24px',
    },
    requiredAsterisk: { color: '#D32F2F' },
    loaderSmall: { width: '14px', height: 'auto' },
    loaderMedium: { width: '24px', height: 'auto', marginRight: '20px' },
    inReviewText: {
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontStyle: 'italic',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.15px',
        lineHeight: '120%',
    },
    trimOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start!important',
        justifyContent: 'start!important',
    },
};

const DEBOUNCE_DELAY = {
    VIN: 1500,
    LICENSE: 1500,
    FLEET: 400,
};

const VehicleDetailsForm = ({ control, errors, watch, setValue, clearErrors, setError, provinceCombinedList, config, customerType, customerId }) => {
    // State
    const [makeString, setMakeString] = useState('');
    const [modelString, setModelString] = useState('');
    const [trimString, setTrimString] = useState('');
    const [trimEngineValue, setTrimEngineValue] = useState(null);
    const [yearHookValue, setYearHookValue] = useState(null);
    const [makeHookValue, setMakeHookValue] = useState(null);
    const [provinceValue, setProvinceValue] = useState('');

    // Refs for decode flags (no re-renders needed)
    const shouldDecodeVIN = useRef(false);
    const shouldDecodeLicense = useRef(false);
    const isDecodedMake = useRef(false);
    const isDecodedModel = useRef(false);
    const isDecodedTrim = useRef(false);

    // Option the user has moved to with the arrow keys (or is hovering). Enter must select
    // this rather than fall through to "create new" just because the typed text is a partial match.
    const highlightedMake = useRef(null);
    const highlightedModel = useRef(null);
    const highlightedTrim = useRef(null);
    const debounceRef = useRef(null);
    const queryClient = useQueryClient();

    // Watch values
    const watchYear = watch('year');
    const watchMake = watch('vehicleMakeId');
    const watchModel = watch('vehicleModelId');
    const watchVin = watch('vin');
    const watchLicense = watch('licenseNumber');
    const watchProvince = watch('provinceId');
    const watchIsFleetCustomer = watch('isFleetCustomer');

    // Debounced values
    const debouncedVinValue = useDebounce(watchVin, DEBOUNCE_DELAY.VIN);
    const debouncedLicenseValue = useDebounce(watchLicense, DEBOUNCE_DELAY.LICENSE);

    // TanStack Query hooks
    const { data: vehicleYearList, isLoading: vehicleYearListLoader } = useGetVehicleYearMasterList({
        page: 1,
        limit: 1000,
    });

    const { data: vehicleMakeList, isLoading: vehicleMakeListLoader } = useGetVehicleMakeList(
        { page: 1, limit: 1000, year: watchYear },
        { enabled: !!watchYear }
    );

    const { data: vehicleModelList, isLoading: vehicleModelListLoader } = useGetVehicleModelList(
        { page: 1, limit: 1000, vehicleMakeId: watchMake, year: watchYear },
        { enabled: !!watchMake && !!watchYear }
    );

    const { data: vehicleModelTrimList, isLoading: vehicleModelTrimListLoader } = useGetVehicleTrimList(
        { page: 1, limit: 1000, vehicleModel: watchModel, vehicleMake: watchMake, year: watchYear },
        { enabled: !!watchModel && !!watchMake && !!watchYear }
    );

    // Mutations
    const decodeVINMutation = useDecodeVIN();
    const decodeLicenseMutation = useDecodeLicense();

    const createMakeMutation = useCreateVehicleMake({
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleMakes({
                    limit: 1000,
                    page: 1,
                    year: watchYear,
                }),
            });
            if (data) {
                setValue('vehicleMakeId', data?._id);
                clearErrors('vehicleMakeId');
                setMakeHookValue(data?._id);
                setMakeString(data?.name);
                clearDependentFields('make');
                toast.success('Make created successfully');
            }
        },
        onError: (error) => {
            console.error('Make creation error:', error);
            toast.error(error?.response?.data?.message || 'Failed to create make');
        },
    });

    const createModelMutation = useCreateVehicleModel({
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleModels({
                    limit: 1000,
                    page: 1,
                    vehicleMakeId: watchMake,
                    year: watchYear,
                }),
            });
            if (data) {
                setValue('vehicleModelId', data?._id);
                clearErrors('vehicleModelId');
                setModelString(data?.name);
                setValue('vehicleModelTrimId', '');
                setTrimString('');
                toast.success('Model created successfully');
            }
        },
        onError: (error) => {
            console.error('Model creation error:', error);
            toast.error('Failed to create model');
        },
    });

    const createTrimMutation = useCreateVehicleTrim({
        onSuccess: (data) => {
            queryClient.refetchQueries({
                queryKey: coreMasterKeys.vehicleTrims({
                    limit: 1000,
                    page: 1,
                    vehicleMake: watchMake,
                    vehicleModel: watchModel,
                    year: watchYear,
                }),
            });
            if (data) {
                setValue('vehicleModelTrimId', data?._id);
                setTrimEngineValue(data);
                toast.success('Trim created successfully');
            }
        },
        onError: (error) => {
            console.error('Trim creation error:', error);
            toast.error('Failed to create trim');
        },
    });

    // Handlers
    const handleDecodeVIN = async () => {
        if (!watchVin || watchVin.length !== 17) {
            if (watchVin && watchVin.length > 0) {
                setError('vin', { message: 'Please enter valid VIN' });
            }
            return;
        }

        clearErrors('vin');

        try {
            const resp = await decodeVINMutation.mutateAsync(watchVin);

            if (resp?.status && resp?.data) {
                const { year, make, model, trim } = resp.data;

                setValue('year', year + '');
                setYearHookValue(year + '');
                setValue('vehicleMakeId', make?._id);
                setMakeHookValue(make?._id);
                setMakeString(make?.name || '');
                setValue('vehicleModelId', model?._id);
                setValue('vehicleModelTrimId', trim?._id);
                setModelString(model?.name || '');
                setTrimString(trim?.name || '');
                setTrimEngineValue(trim || null);

                // Set flags for decoded values
                isDecodedMake.current = !!make?.name;
                isDecodedModel.current = !!model?.name;
                isDecodedTrim.current = !!trim?.name;
            }
        } catch (error) {
            console.error('VIN decode error:', error);
            toast.error('Failed to decode VIN');
        }
    };

    const handleDecodeLicense = async () => {
        if (!watchProvince) {
            setError('provinceId', { message: 'Please select province' });
            return;
        }

        if (!debouncedLicenseValue) return;

        const provinceCode = provinceCombinedList?.find((d) => d._id === watchProvince)?.provinceCode || 'ON';

        try {
            const resp = await decodeLicenseMutation.mutateAsync({
                licenseNumber: debouncedLicenseValue,
                provinceCode,
            });

            if (resp?.status && resp?.data?.vin) {
                setValue('vin', resp.data.vin);
                // Trigger VIN decode after license decode
                shouldDecodeVIN.current = true;
                handleDecodeVIN();
            }
        } catch (error) {
            console.error('License decode error:', error);
            toast.error(error?.response?.data?.message || 'Failed to decode license plate');
        }
    };

    const saveNewVehicleMake = useCallback(async () => {
        if (!makeString?.trim()) return;

        await createMakeMutation.mutateAsync({
            name: makeString,
            year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
        });
    }, [makeString, watchYear, createMakeMutation]);

    const saveNewVehicleModel = useCallback(async () => {
        if (!modelString?.trim()) return;

        await createModelMutation.mutateAsync({
            vehicleModelName: modelString,
            vehicleMakeId: watchMake,
            year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
        });
    }, [modelString, watchMake, watchYear, createModelMutation]);

    const saveNewVehicleTrim = useCallback(async () => {
        if (!trimString?.trim()) return;

        await createTrimMutation.mutateAsync({
            vehicleTrimName: trimString,
            vehicleModelId: watchModel,
            vehicleMakeId: watchMake,
            year: typeof watchYear === 'number' ? watchYear.toString() : watchYear,
        });
    }, [trimString, watchModel, watchMake, watchYear, createTrimMutation]);

    const handleFleetNumberValidation = useCallback(
        async (inputValue) => {
            if (!watchIsFleetCustomer || !inputValue) {
                clearErrors('fleetUnitNumber');
                return false;
            }

            try {
                const resp = await checkFleetNumberExists({ fleetUnitNumber: inputValue });
                if (resp.exist) {
                    setError('fleetUnitNumber', { message: 'Fleet Unit Number already exists' });
                    return true;
                } else {
                    clearErrors('fleetUnitNumber');
                    return false;
                }
            } catch (error) {
                console.error('Fleet number validation error:', error);
                return false;
            }
        },
        [watchIsFleetCustomer, setError, clearErrors]
    );

    const clearDependentFields = useCallback(
        (level) => {
            if (level === 'year') {
                setValue('vehicleMakeId', '');
                setValue('vehicleModelId', '');
                setValue('vehicleModelTrimId', '');
                setMakeHookValue(null);
                setMakeString('');
                setModelString('');
                setTrimString('');
            } else if (level === 'make') {
                setValue('vehicleModelId', '');
                setValue('vehicleModelTrimId', '');
                setModelString('');
                setTrimString('');
            } else if (level === 'model') {
                setValue('vehicleModelTrimId', '');
                setTrimString('');
            }
        },
        [setValue]
    );

    // Effects
    useEffect(() => {
        if (debouncedVinValue && debouncedVinValue.length === 17 && shouldDecodeVIN.current) {
            shouldDecodeVIN.current = false; // Reset flag immediately
            handleDecodeVIN();
        }
    }, [debouncedVinValue]);

    useEffect(() => {
        if (config?.config?.isLicensePlateDecodeEnabled && debouncedLicenseValue && shouldDecodeLicense.current) {
            shouldDecodeLicense.current = false; // Reset flag immediately
            handleDecodeLicense();
        }
    }, [debouncedLicenseValue]);

    useEffect(() => {
        if (provinceCombinedList?.length > 0 && !provinceValue) {
            const defaultProvince = provinceCombinedList.find((d) => d.isDefault === true)?._id;
            if (defaultProvince) {
                setValue('provinceId', defaultProvince);
                setProvinceValue(defaultProvince);
            }
        }
    }, [provinceCombinedList, provinceValue, setValue]);

    // Helper to get autocomplete options
    const getMakeOptions = useCallback(() => {
        if (!vehicleMakeList?.length) {
            return makeString ? [{ _id: 'new', name: makeString }] : [];
        }

        const hasExactMatch = vehicleMakeList.some((item) => item?.name?.toLowerCase() === makeString?.trim()?.toLowerCase());

        if (!makeString || hasExactMatch) {
            return vehicleMakeList;
        }

        return [{ _id: 'new', name: makeString }, ...vehicleMakeList];
    }, [vehicleMakeList, makeString]);

    const getModelOptions = useCallback(() => {
        if (!vehicleModelList?.length) {
            return modelString ? [{ _id: 'new', name: modelString }] : [];
        }

        const hasExactMatch = vehicleModelList.some((item) => item?.name?.toLowerCase() === modelString?.trim()?.toLowerCase());

        if (!modelString || hasExactMatch) {
            return vehicleModelList;
        }

        return [{ _id: 'new', name: modelString }, ...vehicleModelList];
    }, [vehicleModelList, modelString]);

    const getTrimOptions = useCallback(() => {
        if (!vehicleModelTrimList?.length) {
            return trimString ? [{ _id: 'new', description: trimString, name: trimString }] : [];
        }

        const hasExactMatch = vehicleModelTrimList.some((item) => item?.name?.toLowerCase() === trimString?.trim()?.toLowerCase());

        if (!trimString || hasExactMatch) {
            return vehicleModelTrimList;
        }

        return [{ _id: 'new', description: trimString, name: trimString }, ...vehicleModelTrimList];
    }, [vehicleModelTrimList, trimString]);

    return (
        <Grid item container xs={12} spacing={3}>
            {/* VIN */}
            <Grid item xs={12}>
                <Stack spacing={1}>
                    <Typography sx={STYLES.label}>VIN</Typography>
                    <Controller
                        name="vin"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                inputProps={{ maxLength: 17 }}
                                error={!!errors.vin}
                                helperText={errors.vin?.message || ''}
                                onChange={(e) => {
                                    const value = e.target.value?.toUpperCase();
                                    field.onChange(e);
                                    setValue('vin', e.target.value?.toUpperCase());
                                    shouldDecodeVIN.current = true; // Set flag to trigger decode

                                    if (value) {
                                        checkValidVIN(value) ? clearErrors('vin') : setError('vin', { message: 'Please enter valid VIN' });
                                    } else {
                                        clearErrors('vin');
                                    }
                                }}
                                InputProps={{
                                    endAdornment: decodeVINMutation.isPending ? <CircularProgress size={20} /> : null,
                                }}
                                sx={MuiTextFieldWithFocus}
                            />
                        )}
                    />
                </Stack>
            </Grid>

            <Divider textAlign="center" style={STYLES.divider}>
                or
            </Divider>

            {/* Year */}
            <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                    <Typography sx={STYLES.label}>
                        Year <span style={STYLES.requiredAsterisk}> *</span>
                    </Typography>
                    <Controller
                        name="year"
                        control={control}
                        rules={{ required: 'Year is required' }}
                        render={({ field }) => (
                            <>
                                <Select
                                    {...field}
                                    fullWidth
                                    value={yearHookValue || ''}
                                    onChange={(event) => {
                                        field.onChange(event);
                                        setYearHookValue(event.target.value);
                                        clearDependentFields('year');
                                    }}
                                    error={!!errors.year}
                                    MenuProps={{ style: { maxHeight: '400px' } }}
                                    sx={MuiSelectWithFocus}
                                >
                                    {vehicleYearListLoader ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={24} />
                                        </MenuItem>
                                    ) : (
                                        Array.isArray(vehicleYearList) &&
                                        vehicleYearList.map((y) => (
                                            <MenuItem value={y} key={y}>
                                                {y}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                                {errors.year && (
                                    <Typography color="error" variant="caption" display="block">
                                        {errors.year.message}
                                    </Typography>
                                )}
                            </>
                        )}
                    />
                </Stack>
            </Grid>

            {/* Make */}
            <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                    <Typography sx={STYLES.label}>
                        Make <span style={STYLES.requiredAsterisk}> *</span>
                    </Typography>
                    <Controller
                        name="vehicleMakeId"
                        control={control}
                        rules={{ required: 'Make is required' }}
                        render={({ field }) => (
                            <>
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    disableClearable
                                    forcePopupIcon
                                    disabled={!watchYear}
                                    loading={vehicleMakeListLoader || createMakeMutation.isPending}
                                    options={getMakeOptions()}
                                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    value={vehicleMakeList?.find((m) => m._id === field.value) || null}
                                    onBlur={() => {
                                        if (!field.value) {
                                            setMakeString('');
                                        } else {
                                            const selected = vehicleMakeList?.find((m) => m._id === field.value);
                                            if (selected) setMakeString(selected.name);
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (newValue?._id && newValue._id !== 'new') {
                                            clearErrors('vehicleMakeId');
                                            field.onChange(newValue._id);
                                            setMakeHookValue(newValue._id);
                                            setMakeString(newValue.name);
                                            clearDependentFields('make');
                                        }
                                    }}
                                    onHighlightChange={(event, option) => {
                                        highlightedMake.current = option;
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            event.stopPropagation();

                                            // An option is highlighted in the list - take it over anything the text implies
                                            const highlighted = highlightedMake.current;
                                            if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                                clearErrors('vehicleMakeId');
                                                field.onChange(highlighted._id);
                                                setMakeHookValue(highlighted._id);
                                                setMakeString(highlighted.name);
                                                clearDependentFields('make');
                                                highlightedMake.current = null;
                                                setTimeout(() => document.activeElement.blur(), 0);
                                                return;
                                            }

                                            const inputLowerCase = makeString?.trim()?.toLowerCase();
                                            const exactMatch = vehicleMakeList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                            if (exactMatch) {
                                                clearErrors('vehicleMakeId');
                                                field.onChange(exactMatch._id);
                                                setMakeHookValue(exactMatch._id);
                                                setMakeString(exactMatch.name);
                                                clearDependentFields('make');
                                                setTimeout(() => document.activeElement.blur(), 0);
                                            } else if (inputLowerCase) {
                                                saveNewVehicleMake();
                                            }
                                        }
                                    }}
                                    onInputChange={(event, newValue, reason) => {
                                        setMakeString(newValue);
                                        if (reason === 'input') {
                                            isDecodedMake.current = false;
                                            highlightedMake.current = null;
                                        }
                                        if (!newValue) field.onChange(null);
                                    }}
                                    inputValue={makeString}
                                    renderOption={(props, option) => {
                                        if (option._id === 'new') {
                                            return (
                                                <li
                                                    {...props}
                                                    key={`new-make-${option.name}`}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        height: '44px',
                                                        paddingRight: '0px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            marginRight: '8px',
                                                        }}
                                                    >
                                                        {option.name}
                                                    </span>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        style={{
                                                            background: '#2196F326',
                                                            flexShrink: 0,
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            saveNewVehicleMake();
                                                        }}
                                                    >
                                                        {createMakeMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                    </Button>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props} key={option._id}>
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={!!errors.vehicleMakeId}
                                            helperText={errors.vehicleMakeId?.message || ''}
                                            disabled={!watchYear}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {(vehicleMakeListLoader || createMakeMutation.isPending) && watchYear ? (
                                                            <CircularProgress style={STYLES.loaderSmall} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={!watchYear ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                        />
                                    )}
                                    PaperComponent={CustomPaper}
                                />
                                {vehicleMakeList?.find((m) => m._id === watchMake)?.requestStatus === 'in-review' && (
                                    <Typography sx={STYLES.inReviewText}>
                                        <Info sx={{ fontSize: 20, color: '#006CDD' }} /> awaiting Apex Team approval
                                    </Typography>
                                )}
                            </>
                        )}
                    />
                </Stack>
            </Grid>

            {/* Model */}
            <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                    <Typography sx={STYLES.label}>
                        Model <span style={STYLES.requiredAsterisk}> *</span>
                    </Typography>
                    <Controller
                        name="vehicleModelId"
                        control={control}
                        rules={{ required: 'Model is required' }}
                        render={({ field }) => (
                            <>
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    disableClearable
                                    forcePopupIcon
                                    disabled={!watchYear || !watchMake}
                                    loading={vehicleModelListLoader || createModelMutation.isPending}
                                    options={getModelOptions()}
                                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    value={vehicleModelList?.find((m) => m._id === field.value) || null}
                                    onBlur={() => {
                                        if (!field.value) {
                                            setModelString('');
                                        } else {
                                            const selected = vehicleModelList?.find((m) => m._id === field.value);
                                            if (selected) setModelString(selected.name);
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        if (newValue?._id && newValue._id !== 'new') {
                                            clearErrors('vehicleModelId');
                                            field.onChange(newValue._id);
                                            clearDependentFields('model');
                                        }
                                    }}
                                    onHighlightChange={(event, option) => {
                                        highlightedModel.current = option;
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            event.stopPropagation();

                                            // An option is highlighted in the list - take it over anything the text implies
                                            const highlighted = highlightedModel.current;
                                            if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                                clearErrors('vehicleModelId');
                                                field.onChange(highlighted._id);
                                                setModelString(highlighted.name);
                                                clearDependentFields('model');
                                                highlightedModel.current = null;
                                                setTimeout(() => document.activeElement.blur(), 0);
                                                return;
                                            }

                                            const inputLowerCase = modelString?.trim()?.toLowerCase();
                                            const exactMatch = vehicleModelList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                            if (exactMatch) {
                                                clearErrors('vehicleModelId');
                                                field.onChange(exactMatch._id);
                                                clearDependentFields('model');
                                                setTimeout(() => document.activeElement.blur(), 0);
                                            } else if (inputLowerCase) {
                                                saveNewVehicleModel();
                                            }
                                        }
                                    }}
                                    onInputChange={(event, newValue, reason) => {
                                        setModelString(newValue);
                                        if (reason === 'input') {
                                            isDecodedModel.current = false;
                                            highlightedModel.current = null;
                                        }
                                        if (!newValue) field.onChange(null);
                                    }}
                                    inputValue={modelString}
                                    renderOption={(props, option) => {
                                        if (option._id === 'new') {
                                            return (
                                                <li
                                                    {...props}
                                                    key={`new-model-${option.name}`}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        height: '44px',
                                                        paddingRight: '0',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            marginRight: '8px',
                                                        }}
                                                    >
                                                        {option.name}
                                                    </span>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        style={{
                                                            background: '#2196F326',
                                                            flexShrink: 0,
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            saveNewVehicleModel();
                                                        }}
                                                    >
                                                        {createModelMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                    </Button>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li {...props} key={option._id}>
                                                {option.name}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={!!errors.vehicleModelId}
                                            helperText={errors.vehicleModelId?.message || ''}
                                            disabled={!watchYear || !watchMake}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {(vehicleModelListLoader || createModelMutation.isPending) && watchYear && watchMake ? (
                                                            <CircularProgress style={STYLES.loaderSmall} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            sx={!watchYear || !watchMake ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                        />
                                    )}
                                    PaperComponent={CustomPaper}
                                />
                                {vehicleModelList?.find((m) => m._id === watchModel)?.requestStatus === 'in-review' && (
                                    <Typography sx={STYLES.inReviewText}>
                                        <Info sx={{ fontSize: 20, color: '#006CDD' }} /> awaiting Apex Team approval
                                    </Typography>
                                )}
                            </>
                        )}
                    />
                </Stack>
            </Grid>

            {/* Trim */}
            <Grid item xs={12}>
                <Stack spacing={1}>
                    <Typography sx={STYLES.label}>Trim</Typography>
                    <Controller
                        name="vehicleModelTrimId"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                fullWidth
                                freeSolo
                                disableClearable
                                forcePopupIcon
                                loading={vehicleModelTrimListLoader || createTrimMutation.isPending}
                                disabled={!watchYear || !watchMake || !watchModel}
                                options={getTrimOptions()}
                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                isOptionEqualToValue={(option, value) => option._id === value._id}
                                value={vehicleModelTrimList?.find((m) => m._id === field.value) || null}
                                onBlur={() => {
                                    if (!field.value) {
                                        setTrimString('');
                                    } else {
                                        const selected = vehicleModelTrimList?.find((m) => m._id === field.value);
                                        if (selected) setTrimString(selected.name);
                                    }
                                }}
                                onChange={(event, newValue) => {
                                    if (newValue?._id && newValue._id !== 'new') {
                                        clearErrors('vehicleModelTrimId');
                                        field.onChange(newValue._id);
                                        setTrimEngineValue(vehicleModelTrimList?.find((d) => d._id === newValue._id));
                                    }
                                }}
                                onHighlightChange={(event, option) => {
                                    highlightedTrim.current = option;
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        // An option is highlighted in the list - take it over anything the text implies
                                        const highlighted = highlightedTrim.current;
                                        if (highlighted && highlighted._id && highlighted._id !== 'new') {
                                            clearErrors('vehicleModelTrimId');
                                            field.onChange(highlighted._id);
                                            setTrimString(highlighted.name);
                                            setTrimEngineValue(highlighted);
                                            highlightedTrim.current = null;
                                            setTimeout(() => document.activeElement.blur(), 0);
                                            return;
                                        }

                                        const inputLowerCase = trimString?.trim()?.toLowerCase();
                                        const exactMatch = vehicleModelTrimList?.find((option) => option.name.toLowerCase() === inputLowerCase);

                                        if (exactMatch) {
                                            clearErrors('vehicleModelTrimId');
                                            field.onChange(exactMatch._id);
                                            setTrimEngineValue(exactMatch);
                                            setTimeout(() => document.activeElement.blur(), 0);
                                        } else if (inputLowerCase) {
                                            saveNewVehicleTrim();
                                        }
                                    }
                                }}
                                onInputChange={(event, newValue, reason) => {
                                    setTrimString(newValue);
                                    if (reason === 'input') {
                                        isDecodedTrim.current = false;
                                        highlightedTrim.current = null;
                                    }
                                    if (!newValue) field.onChange(null);
                                }}
                                inputValue={trimString}
                                renderOption={(props, option) => {
                                    if (option._id === 'new') {
                                        return (
                                            <li
                                                {...props}
                                                key={`new-trim-${option.name}`}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    height: '44px',
                                                    paddingRight: '0',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        marginRight: '8px',
                                                    }}
                                                >
                                                    {option.name}
                                                </div>
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    style={{
                                                        background: '#2196F326',
                                                        flexShrink: 0,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        saveNewVehicleTrim();
                                                    }}
                                                >
                                                    {createTrimMutation.isPending ? <CircularProgress disableShrink size={18} /> : 'Save'}
                                                </Button>
                                            </li>
                                        );
                                    }
                                    return (
                                        <Box component="li" {...props} key={option._id} sx={STYLES.trimOption}>
                                            <div style={{ fontWeight: 500 }}>{option?.name}</div>
                                            {option?.description && <div>{option.description}</div>}
                                        </Box>
                                    );
                                }}
                                PaperComponent={CustomPaper}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={!!errors.vehicleModelTrimId}
                                        helperText={errors.vehicleModelTrimId?.message || ''}
                                        disabled={!watchYear || !watchMake || !watchModel}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {(vehicleModelTrimListLoader || createTrimMutation.isPending) && watchYear && watchMake && watchModel ? (
                                                        <CircularProgress style={STYLES.loaderSmall} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={!watchYear || !watchMake || !watchModel ? MuiDisabledTextField : MuiTextFieldWithFocus}
                                    />
                                )}
                            />
                        )}
                    />
                </Stack>
            </Grid>

            <Divider textAlign="center" style={STYLES.divider}>
                and
            </Divider>

            {/* License Plate */}
            <LicensePlateInputV4
                control={control}
                errors={errors}
                config={config}
                provinces={provinceCombinedList}
                provinceValue={provinceValue}
                setProvinceValue={(value) => {
                    setProvinceValue(value);
                    setValue('provinceId', value);
                }}
                provinceLoader={false}
                isFleetCustomer={watchIsFleetCustomer}
                getValues={watch}
                setValueInForm={setValue}
                clearErrors={clearErrors}
                shouldDecodeLicense={shouldDecodeLicense}
                handleDecodeLicense={handleDecodeLicense}
                decodeLicenseLoader={decodeLicenseMutation.isPending}
                isV4={true}
            />

            {/* Fleet Unit */}
            {watchIsFleetCustomer && (
                <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                        <Typography sx={STYLES.label}>Fleet Unit #</Typography>
                        <Controller
                            name="fleetUnitNumber"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    error={!!errors.fleetUnitNumber}
                                    helperText={errors.fleetUnitNumber?.message || ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        field.onChange(inputValue);

                                        if (debounceRef.current) clearTimeout(debounceRef.current);

                                        if (inputValue && customerType === USER_TYPES.SHOP_CREATED && customerId) {
                                            debounceRef.current = setTimeout(() => {
                                                handleFleetNumberValidation(inputValue);
                                            }, DEBOUNCE_DELAY.FLEET);
                                        } else {
                                            clearErrors('fleetUnitNumber');
                                        }
                                    }}
                                    sx={MuiTextFieldWithFocus}
                                />
                            )}
                        />
                    </Stack>
                </Grid>
            )}
        </Grid>
    );
};

export default VehicleDetailsForm;
